export const CHAIN_LABELS = { solana: 'SOL', base: 'BASE', ethereum: 'ETH', bsc: 'BSC', robinhood: 'RH' };
export const CHAIN_COLORS = { solana: 'purple', base: 'blue', ethereum: 'indigo', bsc: 'yellow', robinhood: 'green' };
export const CHAIN_DEXSCREENER = { solana: 'solana', base: 'base', ethereum: 'ethereum', bsc: 'bsc', robinhood: 'robinhood' };

export const NATIVE_SYMBOLS = { solana: 'SOL', base: 'ETH', ethereum: 'ETH', bsc: 'BNB', robinhood: 'ETH' };

export const DEFAULT_EMA_SIZE_BY_CHAIN = { solana: 0.5, base: 0.05, ethereum: 0.05, bsc: 0.5, robinhood: 0.05 };

export function getNativeSymbol(chain) {
  return NATIVE_SYMBOLS[chain] || 'ETH';
}

export function resolveEmaBuyAmount(item, emaEntry = {}) {
  if (item?.positionSizeNative != null && Number(item.positionSizeNative) > 0) {
    return Number(item.positionSizeNative);
  }
  const chain = item?.chain || 'solana';
  const byChain = emaEntry.positionSizeByChain?.[chain];
  if (byChain != null && Number(byChain) > 0) return Number(byChain);
  if (emaEntry.positionSizeNative != null && Number(emaEntry.positionSizeNative) > 0) {
    return Number(emaEntry.positionSizeNative);
  }
  return DEFAULT_EMA_SIZE_BY_CHAIN[chain] ?? 0.05;
}

export const STRATEGY_LABELS = { recovery: 'Recovery', pump: 'Pump', volume: 'Volume', ema: 'EMA' };

export function buildChartUrl(chain, address) {
  const c = CHAIN_DEXSCREENER[chain] || chain;
  return `https://dexscreener.com/${c}/${address}?embed=1&theme=dark&trades=0&info=0`;
}

export function buildAxiomUrl(chain, address) {
  return `https://axiom.trade/t/${address}`;
}

export function buildGmgnUrl(chain, address) {
  const c = chain === 'solana' ? 'sol' : chain === 'base' ? 'base' : chain === 'ethereum' ? 'eth' : chain;
  return `https://gmgn.ai/${c}/token/${address}`;
}

const FOMO_CHAIN_SLUGS = { solana: 'solana', ethereum: 'ethereum', base: 'base', bsc: 'bnb', robinhood: 'robinhood' };
export function buildFomoUrl(chain, address) {
  const slug = FOMO_CHAIN_SLUGS[chain];
  if (!slug || !address) return null;
  return `https://fomo.family/tokens/${slug}/${address}`;
}

export function buildDexScreenerUrl(chain, address) {
  const c = CHAIN_DEXSCREENER[chain] || chain;
  return `https://dexscreener.com/${c}/${address}`;
}

export function getStrategyMeta(p) {
  const strategy = p.strategy
    || (p.entryReason === 'volume_signal' ? 'volume'
      : p.entryReason === 'pump_signal' ? 'pump'
        : p.entryReason === 'ema_signal' ? 'ema' : 'recovery');
  const map = {
    pump: {
      badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
      chart: 'bg-purple-900/50 text-purple-400 border border-purple-700',
      label: 'P',
      full: 'PUMP',
    },
    volume: {
      badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
      chart: 'bg-orange-900/50 text-orange-400 border border-orange-700',
      label: 'V',
      full: 'VOLUME',
    },
    ema: {
      badge: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400',
      chart: 'bg-cyan-900/50 text-cyan-400 border border-cyan-700',
      label: 'E',
      full: 'EMA',
    },
    recovery: {
      badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      chart: 'bg-green-900/50 text-green-400 border border-green-700',
      label: 'R',
      full: 'RECOVERY',
    },
  };
  return { strategy, ...(map[strategy] || map.recovery) };
}

export function formatTime(ts) {
  if (!ts) return '-';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  if (diffMs < 60000) return 'just now';
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
  return d.toLocaleDateString();
}

export function getExitConfigForPosition(position, settings = {}) {
  if (position?.entryReason === 'pump_signal') {
    return settings.pumpExit || {};
  }
  if (position?.entryReason === 'volume_signal') {
    return settings.volumeExit || settings.exit || {};
  }
  if (position?.entryReason === 'ema_signal') {
    return settings.emaExit || {};
  }
  return settings.exit || {};
}

/** Manual sell quick buttons — standard presets plus exit tier sell %. */
export function getManualSellPercents(position, settings = {}) {
  const tiers = getExitConfigForPosition(position, settings).tiers || [];
  const fromTiers = [...new Set(
    tiers
      .map((t) => Math.min(100, Math.max(1, parseInt(t.sellPercent, 10) || 0)))
      .filter((n) => n > 0 && n <= 100)
  )];

  return [...new Set([25, 50, 75, 100, ...fromTiers])].sort((a, b) => a - b);
}

export function formatNum(n, decimals = 4) {
  if (n === null || n === undefined) return '-';
  return Number(n).toFixed(decimals);
}

/** Compact USD market-cap formatting, e.g. $12.7K · $1.24M · $1.5B */
export function formatMC(n) {
  const v = Number(n);
  if (!v || !Number.isFinite(v) || v <= 0) return '-';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

export function formatActivityLabel(a) {
  if (a.type === 'entry') return 'Opened';
  if (a.type === 'exit_tier') return `T${a.tier} (${a.multiple}x)`;
  if (a.type === 'stop_loss') return 'Stop Loss';
  if (a.type === 'trailing_stop') return 'Trail Stop';
  return a.action || a.type;
}

export function activityDotColor(type) {
  if (type === 'entry') return 'bg-blue-500';
  if (type === 'exit_tier') return 'bg-green-500';
  if (type === 'stop_loss') return 'bg-red-500';
  return 'bg-orange-500';
}
